import React, { useEffect, useState } from 'react';

import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { CompanyUiModel } from '../../../shared/ui-models/company.ui-model';
import { Delete, Edit, Folder, Mail, Phone, User } from '../../../ui/Icons';
import {
  addNewCompany,
  deleteCompany,
  getAllCompanies,
  updateCompany
} from '../../shared/services/data.service';
import { AddCompanyModal } from './components/add-company-modal/add-company-modal.component';
import { EditCompanyModal } from './components/edit-company-modal/edit-company-modal.component';
import { DeleteCompanyModal } from './components/delete-company-modal/delete-company-modal.component';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
export const CompaniesListContainer = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companiesList, setCompaniesList] = useState<CompanyUiModel[]>([]);
  const [companyToUpdate, setCompanyToUpdate] = useState<CompanyUiModel>();
  const [companyToDelete, setCompanyToDelete] = useState<CompanyUiModel>();

  useEffect(() => {
    getAllCompanies().then((response: CompanyUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setCompaniesList(response);
      }
    });
  }, []);

  const handleCompanyAdd = (company: CompanyUiModel) => {
    const promise = addNewCompany(company).then((response: CompanyUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setCompaniesList([...companiesList, response]);
    });

    birdToast.promise(promise, {
      loadingMessage: 'Adding company...',
      successMessage: 'Company added successfully',
      errorPermissionMessage: 'You do not have permission to add company',
      errorFailedMessage: 'Failed to add company'
    });
  };

  const handleCompanyEdit = (companyId: string, company: Partial<CompanyUiModel>) => {
    const promise = updateCompany(companyId, company).then(
      (response: CompanyUiModel | ErrorInfo) => {
        if (isErrorInfo(response)) {
          throw response;
        }
        const updatedCompanies = companiesList.map((item) =>
          item.id === response.id ? response : item
        );
        setCompaniesList(updatedCompanies);
      }
    );

    birdToast.promise(promise, {
      loadingMessage: 'Updating company...',
      successMessage: 'Company updated successfully',
      errorPermissionMessage: 'You do not have permission to update company',
      errorFailedMessage: 'Failed to update company'
    });
  };

  const handleCompanyDelete = (company: CompanyUiModel) => {
    if (!company.id) return;
    const promise = deleteCompany(company.id).then((response: {} | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setCompaniesList(companiesList.filter((item) => item.id !== company.id));
    });

    birdToast.promise(promise, {
      loadingMessage: 'Deleting company...',
      successMessage: 'Company deleted successfully',
      errorPermissionMessage: 'You do not have permission to delete company',
      errorFailedMessage: 'Failed to delete company'
    });
  };

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="my-4 text-4xl font-recoleta font-bold text-primary-600">Companies</div>
        <Button
          type={ButtonType.primary}
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          Add Company
        </Button>
      </div>
      <div className="grid h-full  w-full items-stretch gap-[1.6rem] pb-[2rem] [grid-template-columns:1fr] md:[grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
        {companiesList.map((company, index) => (
          <div
            key={company.id ? company.id : `company-${index}`}
            className="group relative flex w-full flex-col justify-between overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="p-5">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex gap-2 items-center ">
                  <Folder size="18px" />{' '}
                  <h3 className="truncate text-2xl font-bold text-neutral-500" title={company.name}>
                    {company.name || 'Untitled Company'}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    aria-label="Edit company"
                    className="flex size-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-primary-50 hover:text-primary-600 hover:ring-primary-200"
                    onClick={() => {
                      setCompanyToUpdate(company);
                      setShowEditModal(true);
                    }}
                  >
                    <Edit size="16px" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete company"
                    className="flex size-10 items-center justify-center rounded-full bg-white text-gray-400 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-rose-50 hover:text-rose-600 hover:ring-rose-200"
                    onClick={() => {
                      setCompanyToDelete(company);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Delete size="16px" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 border-t border-gray-50 pt-3">
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <User size="16px" />
                  </div>
                  <span className="truncate" title={company.contactPersonName}>
                    {company.contactPersonName || 'No Contact Person'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <Mail size="16px" />
                  </div>
                  <span className="truncate" title={company.contactPersonEmail}>
                    {company.contactPersonEmail || 'No Email'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xl text-neutral-500">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                    <Phone size="16px" />
                  </div>
                  <span className="truncate">{company.contactPersonPhoneNumber || '-'}</span>
                </div>
              </div>
            </div>
            <div className="h-1 w-full bg-gradient-to-r from-primary-50 via-primary-100 to-white opacity-50" />
          </div>
        ))}
      </div>

      {showAddModal && (
        <AddCompanyModal
          config={{ showModal: showAddModal }}
          eventHandlers={{
            cancelHandler: () => setShowAddModal(false),
            addCompanyHandler: (company) => {
              handleCompanyAdd(company);
              setShowAddModal(false);
            }
          }}
        />
      )}
      {showEditModal && (
        <EditCompanyModal
          data={{ company: companyToUpdate }}
          config={{ showModal: showEditModal }}
          eventHandlers={{
            cancelHandler: () => setShowEditModal(false),
            editCompanyHandler: (companyId, company) => {
              handleCompanyEdit(companyId, company);
              setShowEditModal(false);
            }
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteCompanyModal
          data={{ company: companyToDelete }}
          config={{ showModal: showDeleteModal }}
          eventHandlers={{
            cancelHandler: () => setShowDeleteModal(false),
            deleteCompanyHandler: (company) => {
              handleCompanyDelete(company);
              setShowDeleteModal(false);
            }
          }}
        />
      )}
    </div>
  );
};
